import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Menu", id: "menu" },
  { label: "Order", id: "order" },
  { label: "Catering", id: "catering" },
  { label: "Reviews", id: "reviews" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
        scrolled ? "bg-cream/80 backdrop-blur-xl border-b border-ink/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("hero")}
          className={`font-serif-display text-3xl tracking-tight leading-none ${scrolled ? "text-ink" : "text-cream"}`}>
          Sero's<span className="text-terracotta">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)}
              className={`text-sm tracking-wide relative group ${scrolled ? "text-ink/80" : "text-cream/90"}`}>
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={BUSINESS.phoneHref} data-testid="nav-call-btn"
            className={`hidden sm:flex items-center gap-2 text-sm ${scrolled ? "text-ink" : "text-cream"}`}>
            <Phone size={16} className="text-terracotta" /> {BUSINESS.phone}
          </a>
          <button onClick={() => go("order")} data-testid="nav-order-btn"
            className="hidden md:inline-flex bg-terracotta text-cream text-sm px-6 py-2.5 rounded-full hover:bg-ink transition-colors duration-300 active:scale-95">
            Order Online
          </button>
          <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle"
            className={`md:hidden ${scrolled ? "text-ink" : "text-cream"}`}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-cream border-t border-ink/10">
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => go(l.id)}
                  className="text-left text-ink text-lg font-serif-display">{l.label}</button>
              ))}
              <a href={BUSINESS.phoneHref} className="text-terracotta font-medium">{BUSINESS.phone}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
